import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  TextField,
  MenuItem,
  Grid2 as Grid,
} from '@mui/material';
import { formatDayHeader, formatMonthYear } from '../utils/formatters';
import { 
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAppointments, useSchedules } from '../hooks/queries';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import ClassDetailsModal from '../components/ClassDetailsModal';
import AppointmentCard from '../components/AppointmentCard';
import ClassAppointmentCard from '../components/ClassAppointmentCard';
import { useContextualHint } from '../hooks/useContextualHint';
import { useSnackbar } from '../hooks/useSnackbar';
import { useDialog } from '../hooks/useDialog';
import PageLayout from '../components/ui/PageLayout';
import PageFilters from '../components/ui/PageFilters';
import AppSnackbar from '../components/ui/AppSnackbar';
import ConfirmDialog from '../components/ui/ConfirmDialog';

// Helper function defined outside component
function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day; // domingo = 0
  return new Date(d.setDate(diff));
}

export default function AppointmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const daysToShow = isMobile ? 2 : 7;

  // Estados de navegação e UI
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Filtros (estados locais - não afetam a query)
  const [selectedProfessional, setSelectedProfessional] = useState('all');
  const [selectedPlace, setSelectedPlace] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // TODO 2.8: Filtro chip para mostrar apenas turmas
  const [showOnlyClasses, setShowOnlyClasses] = useState(false);

  // Dialogs e notificações
  const confirmDialog = useDialog();
  const markAttendedDialog = useDialog();
  const cancelDialog = useDialog();
  const detailsDialog = useDialog();
  const classDetailsDialog = useDialog(); // TODO 2.5: Modal de detalhes da turma
  const [rescheduleData, setRescheduleData] = useState(null);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Calcula datas para a query
  const queryDates = useMemo(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow);
    return {
      startDate: currentWeekStart.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }, [currentWeekStart, daysToShow]);

  // React Query hook - SEM CACHE (sempre busca dados frescos)
  const {
    appointments,
    professionals,
    places,
    loading,
    error,
    reload,
    confirmAsync,
    cancelAsync,
    markAttendedAsync,
  } = useAppointments(queryDates);

  // TODO 2.3: Carregar schedules para acessar maxAttendees ao agrupar appointments
  const { schedules } = useSchedules();

  // TODO 2.3: Criar mapa schedulesById para rápido acesso
  const schedulesById = useMemo(() => {
    const map = {};
    for (const schedule of schedules) {
      map[schedule.id] = schedule;
    }
    return map;
  }, [schedules]);

  const hint = useContextualHint('appointments', {});

  const handlePreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  };

  const handleAppointmentCreated = () => {
    setShowForm(false);
    reload();
  };

  const getDaysArray = () => {
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getAppointmentsForDay = (date) => {
    // Converter data do calendário para string YYYY-MM-DD em UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    let filtered = appointments.filter(apt => {
      if (!apt.startTime) return false; // Skip appointments sem startTime
      
      // Converter appointment UTC para BRT (UTC-3)
      const aptDate = new Date(apt.startTime);
      if (isNaN(aptDate.getTime())) return false; // Skip datas inválidas
      
      // Ajustar para timezone BRT (UTC-3)
      const aptDateBRT = new Date(aptDate.getTime() - (3 * 60 * 60 * 1000));
      const aptYear = aptDateBRT.getUTCFullYear();
      const aptMonth = String(aptDateBRT.getUTCMonth() + 1).padStart(2, '0');
      const aptDay = String(aptDateBRT.getUTCDate()).padStart(2, '0');
      const aptDateStr = `${aptYear}-${aptMonth}-${aptDay}`;
      
      return aptDateStr === dateStr;
    });
    
    // Aplicar filtro de profissional
    if (selectedProfessional !== 'all') {
      filtered = filtered.filter(apt => 
        apt.schedule?.professionalName && 
        professionals.find(p => p.id === selectedProfessional)?.name === apt.schedule.professionalName
      );
    }
    
    // Aplicar filtro de local
    if (selectedPlace !== 'all') {
      filtered = filtered.filter(apt => 
        apt.schedule?.placeName && 
        places.find(p => p.id === selectedPlace)?.name === apt.schedule.placeName
      );
    }
    
    // ✅ Aplicar filtro de status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(apt => {
        const status = getAppointmentStatus(apt);
        return status === selectedStatus;
      });
    }
    
    return filtered;
  };

  // ✅ Helper para determinar status do appointment
  const getAppointmentStatus = (appointment) => {
    if (appointment.cancelledAt) return 'cancelled';
    if (appointment.attendedAt) return 'completed';
    
    // Se passou da hora e não foi atendido = no_show
    const appointmentTime = new Date(appointment.startTime);
    const now = new Date();
    if (now > appointmentTime && !appointment.attendedAt && appointment.confirmedAt) {
      return 'no_show';
    }
    
    if (appointment.confirmedAt) return 'confirmed';
    return 'pending';
  };

  // Determina se o usuário pode escrever neste agendamento.
  // Para profissionais próprios: sempre true.
  // Para profissionais compartilhados: precisa de permissão ReadWrite em Appointments (entityType=2, level=2).
  const getApptCanWrite = (apt) => {
    if (!apt?.schedule?.professionalId) return true;
    const prof = professionals.find(p => p.id === apt.schedule.professionalId);
    if (!prof || !prof.isShared) return true;
    return (prof.permissions?.[2] ?? 0) >= 2;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  // TODO 2.3: Agrupar appointments por (scheduleId, startTime) quando turma
  const groupAppointmentsBySlot = (aptsArray) => {
    const map = new Map();
    for (const apt of aptsArray) {
      const schedule = schedulesById[apt.scheduleId];
      const isClass = schedule?.maxAttendees > 1;
      const key = isClass
        ? `class::${apt.scheduleId}::${apt.startTime}`
        : `solo::${apt.id}`;
      if (!map.has(key)) map.set(key, { isClass, schedule, items: [], slot: apt.startTime });
      map.get(key).items.push(apt);
    }
    return Array.from(map.values());
  };

  // TODO 2.8: Filtrar grupos por showOnlyClasses
  const filterGroupsByClasses = (groups) => {
    if (!showOnlyClasses) return groups;
    return groups.filter(g => g.isClass === true);
  };

  // =============== AÇÕES DE APPOINTMENT ===============

  const handleConfirmClick = (appointment) => {
    confirmDialog.handleOpen(appointment);
  };

  const handleConfirmAppointment = async () => {
    const appointment = confirmDialog.data;
    try {
      await confirmAsync(appointment.id);
      showSuccess('Agendamento confirmado com sucesso!');
    } catch (err) {
      console.error('Erro ao confirmar agendamento:', err);
      showError(err.response?.data?.message || 'Erro ao confirmar agendamento');
    } finally {
      confirmDialog.handleClose();
    }
  };

  const handleMarkAttendedClick = (appointment) => {
    markAttendedDialog.handleOpen(appointment);
  };

  const handleMarkAttended = async () => {
    const appointment = markAttendedDialog.data;
    if (!appointment) return;
    try {
      await markAttendedAsync(appointment.id);
      showSuccess('Agendamento marcado como atendido!');
    } catch (err) {
      console.error('Erro ao marcar como atendido:', err);
      showError(err.response?.data?.message || 'Erro ao marcar como atendido');
    } finally {
      markAttendedDialog.handleClose();
    }
  };

  const handleCancelClick = (appointment) => {
    cancelDialog.handleOpen(appointment);
  };

  const handleCancelAppointment = async () => {
    const appointment = cancelDialog.data;
    if (!appointment) return;
    try {
      await cancelAsync({ id: appointment.id, reason: 'Cancelado pelo profissional' });
      showSuccess('Agendamento cancelado com sucesso!');
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      showError(err.response?.data?.message || 'Erro ao cancelar agendamento');
    } finally {
      cancelDialog.handleClose();
    }
  };

  const handleViewDetails = (appointmentOrGroup) => {
    // TODO 2.5: Determinar se é um appointment ou um group de turma
    if (appointmentOrGroup.isClass !== undefined) {
      // É um group (do groupAppointmentsBySlot)
      classDetailsDialog.handleOpen(appointmentOrGroup);
    } else {
      // É um appointment individual
      detailsDialog.handleOpen(appointmentOrGroup);
    }
  };

  const handleRescheduleClick = (appointment) => {
    // Abrir modal de agendamento com dados pré-preenchidos
    setRescheduleData({
      clientName: appointment.clientName,
      clientPhone: appointment.clientPhone,
      clientEmail: appointment.clientEmail,
      clientNotes: appointment.clientNotes
    });
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setRescheduleData(null);
    setSelectedDate(null);
  };

  const days = getDaysArray();

  return (
    <PageLayout
      themeKey="appointments"
      actionLabel="Novo Agendamento"
      onAction={() => setShowForm(true)}
      hint={hint}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <PageFilters
        onClear={() => {
          setSelectedProfessional('all');
          setSelectedPlace('all');
          setSelectedStatus('all');
          // TODO 2.8: Resetar filtro de turmas ao limpar
          setShowOnlyClasses(false);
        }}
        clearDisabled={selectedProfessional === 'all' && selectedPlace === 'all' && selectedStatus === 'all' && !showOnlyClasses}
      >
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Profissional"
            value={selectedProfessional}
            onChange={(e) => setSelectedProfessional(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            {professionals.map((prof) => (
              <MenuItem key={prof.id} value={prof.id}>{prof.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Local"
            value={selectedPlace}
            onChange={(e) => setSelectedPlace(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            {places.map((place) => (
              <MenuItem key={place.id} value={place.id}>{place.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="pending">🟠 Não Confirmados</MenuItem>
            <MenuItem value="confirmed">🟢 Confirmados</MenuItem>
            <MenuItem value="completed">🔵 Concluídos</MenuItem>
            <MenuItem value="no_show">🔴 Não Compareceram</MenuItem>
            <MenuItem value="cancelled">⚪ Cancelados</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="flex-start" sx={{ pt: 1 }}>
            {/* TODO 2.8: Chip-filtro para mostrar apenas turmas */}
            <Chip
              label="Apenas Turmas"
              variant={showOnlyClasses ? 'filled' : 'outlined'}
              color={showOnlyClasses ? 'primary' : 'default'}
              onClick={() => setShowOnlyClasses(!showOnlyClasses)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Grid>
      </PageFilters>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <IconButton onClick={handlePreviousWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          
          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handleToday}>
              Hoje
            </Button>
            <Typography variant="body1" fontWeight="medium">
              {formatMonthYear(currentWeekStart)}
            </Typography>
          </Box>

          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <Box 
            display="grid" 
            gridTemplateColumns={`repeat(${daysToShow}, 1fr)`} 
            gap={1}
          >
            {days.map((day, index) => {
              const { weekday, dayNum } = formatDayHeader(day);
              const dayAppointments = getAppointmentsForDay(day);
              
              return (
                <Box 
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: isToday(day) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: isToday(day) ? 'primary.50' : 'background.paper'
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 1, 
                      textAlign: 'center',
                      bgcolor: isToday(day) ? 'primary.main' : 'grey.100',
                      color: isToday(day) ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="caption" display="block" textTransform="capitalize">
                      {weekday}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {dayNum}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1, minHeight: 200 }}>
                    {dayAppointments.length === 0 ? (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        textAlign="center" 
                        display="block"
                        sx={{ mt: 4 }}
                      >
                        Sem agendamentos
                      </Typography>
                    ) : (
                      // TODO 2.3: Agrupar appointments por (scheduleId, startTime) quando turma
                      // TODO 2.8: Aplicar filtro de turmas se showOnlyClasses estiver ativo
                      filterGroupsByClasses(groupAppointmentsBySlot(dayAppointments)).map((g) =>
                        g.isClass
                          ? (
                            // TODO 2.4: ClassAppointmentCard exibe resumo do grupo
                            <ClassAppointmentCard
                              key={`class_${g.items[0].id}`}
                              group={g}
                              onClick={handleViewDetails}
                            />
                          )
                          : (
                            <AppointmentCard
                              key={g.items[0].id}
                              appointment={g.items[0]}
                              canWrite={getApptCanWrite(g.items[0])}
                              onConfirm={handleConfirmClick}
                              onCancel={handleCancelClick}
                              onReschedule={handleRescheduleClick}
                              onMarkAttended={handleMarkAttendedClick}
                              onViewDetails={handleViewDetails}
                            />
                          )
                      )
                    )}
                    
                    {/* Botão para adicionar novo agendamento nesta data */}
                    {!isPastDay(day) && (
                      <Box 
                        sx={{ 
                          mt: 1, 
                          pt: 1, 
                          borderTop: '1px dashed',
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => {
                            setSelectedDate(day);
                            setShowForm(true);
                          }}
                          sx={{ 
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.50'
                            }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Modal de detalhes completos do agendamento */}
      <AppointmentDetailsModal
        open={detailsDialog.open}
        appointmentId={detailsDialog.data?.id}
        onClose={detailsDialog.handleClose}
        canWrite={getApptCanWrite(detailsDialog.data)}
        onConfirm={(apt) => handleConfirmClick(apt)}
        onMarkAttended={(apt) => handleMarkAttendedClick(apt)}
        onCancel={(apt) => handleCancelClick(apt)}
        onReschedule={(apt) => handleRescheduleClick(apt)}
      />

      {/* TODO 2.5: Modal de detalhes da turma com lista de participantes */}
      <ClassDetailsModal
        open={classDetailsDialog.open}
        group={classDetailsDialog.data}
        onClose={classDetailsDialog.handleClose}
        canWrite={true}
        onConfirm={(apt) => handleConfirmClick(apt)}
        onMarkAttended={(apt) => handleMarkAttendedClick(apt)}
        onCancel={(apt) => handleCancelClick(apt)}
      />

      {/* Modal auto-suficiente - carrega próprios dados */}
      <AppointmentFormDialog
        open={showForm}
        onClose={handleFormClose}
        onSuccess={handleAppointmentCreated}
        initialData={rescheduleData}
        preselectedDate={selectedDate}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={confirmDialog.handleClose}
        onConfirm={handleConfirmAppointment}
        title="Confirmar Agendamento"
        message={<>Tem certeza que deseja confirmar o agendamento de <strong>{confirmDialog.data?.clientName}</strong>?</>}
        confirmText="Confirmar"
        confirmColor="success"
      />

      <ConfirmDialog
        open={markAttendedDialog.open}
        onClose={markAttendedDialog.handleClose}
        onConfirm={handleMarkAttended}
        title="Marcar como Atendido"
        message={<>Confirmar que <strong>{markAttendedDialog.data?.clientName}</strong> compareceu ao agendamento?</>}
        confirmText="Marcar como Atendido"
        confirmColor="info"
      />

      <ConfirmDialog
        open={cancelDialog.open}
        onClose={cancelDialog.handleClose}
        onConfirm={handleCancelAppointment}
        title="Cancelar Agendamento"
        message={<>Tem certeza que deseja cancelar o agendamento de <strong>{cancelDialog.data?.clientName}</strong>? Esta ação não pode ser desfeita.</>}
        confirmText="Sim, Cancelar"
        cancelText="Não"
        confirmColor="error"
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
